import React from 'react';
import { Alert, Card, Col, Radio, Row, Space, Spin, Tag, Button, Tooltip, Typography } from 'antd';
import {
  CloseCircleOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  AI_FEATURE_ENGINE,
  getFeatureDisplayName,
  getFeatureEngine,
  getFeatureEngineLabel,
  getFeatureShortLabel,
} from '../../utils/aiFeatureMetadata';

const { Text } = Typography;

const metricValueStyle = { fontSize: 20, fontWeight: 600, lineHeight: "26px", margin: "2px 0 0" };

export const DashboardCards = ({
  aiUsageData,
  showAiOverallDetails,
  setShowAiOverallDetails,
  showAiProviderDetails,
  setShowAiProviderDetails,
  showAiModelDetails,
  setShowAiModelDetails,
  showAiFeatureDetails,
  setShowAiFeatureDetails,
  showAiInternalDetails,
  setShowAiInternalDetails,
  showAiCustomerDetails,
  setShowAiCustomerDetails,
  aiUsageClass = "customer",
  onAiUsageClassChange,
  aiWindowLabel = "Last 30 days",
  aiLoading = false,
  aiError = null,
}) => {
  const formatCost = (cost) => {
    if (!cost || cost === 0) return '$0.00';
    if (cost < 0.0001) return `$${cost.toExponential(2)}`;
    if (cost < 0.01) return `$${cost.toFixed(6)}`;
    return `$${cost.toFixed(4)}`;
  };

  const formatNumber = (num) => (num != null ? num.toLocaleString() : '0');

  const getFeatureTagColor = (engine) =>
    engine === AI_FEATURE_ENGINE.POLARS ? 'green' : 'purple';

  const buildFeatureTooltip = (featureKey, stats) => {
    const engine = getFeatureEngine(featureKey);
    const lines = [
      getFeatureDisplayName(featureKey),
      getFeatureEngineLabel(engine),
    ];
    if (stats) {
      lines.push(`${formatNumber(stats.total_requests)} reqs · ${formatCost(stats.total_cost)}`);
    }
    return lines.join('\n');
  };

  const renderFeatureTag = (featureKey, stats = null) => {
    const engine = getFeatureEngine(featureKey);
    return (
      <Tooltip title={buildFeatureTooltip(featureKey, stats)}>
        <Tag
          color={getFeatureTagColor(engine)}
          style={{ margin: 0, maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}
        >
          {getFeatureShortLabel(featureKey)}
        </Tag>
      </Tooltip>
    );
  };

  const partitionFeaturesByEngine = (features) => {
    const polars = [];
    const paid = [];
    features.forEach((f) => {
      if (getFeatureEngine(f.feature) === AI_FEATURE_ENGINE.POLARS) {
        polars.push(f);
      } else {
        paid.push(f);
      }
    });
    return { polars, paid };
  };

  const renderFeatureDetailRow = (featureKey, stats) => {
    const engine = getFeatureEngine(featureKey);
    return (
      <div
        key={featureKey}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}
      >
        <Tooltip title={getFeatureDisplayName(featureKey)}>
          <Space size={4} style={{ flex: 1, minWidth: 0, maxWidth: '55%' }}>
            <Tag color={getFeatureTagColor(engine)} style={{ margin: 0, fontSize: '9px', lineHeight: '16px', padding: '0 4px' }}>
              {engine === AI_FEATURE_ENGINE.POLARS ? 'Smart AI Summary' : 'LLM'}
            </Tag>
            <Text style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
              {getFeatureShortLabel(featureKey)}
            </Text>
          </Space>
        </Tooltip>
        <Space size={4}>
          <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>{formatCost(stats.total_cost)}</Tag>
          <Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>{formatNumber(stats.total_requests)} reqs</Tag>
        </Space>
      </div>
    );
  };

  const renderSummaryRow = (label, value, suffix = null) => (
    <div key={String(label)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px' }}>
      <Text style={{ fontSize: '11px', flex: 1, minWidth: 0 }}>{label}</Text>
      <Space size={6}>
        <Text strong style={{ fontSize: '11px' }}>{value}</Text>
        {suffix}
      </Space>
    </div>
  );

  const internalSummary = aiUsageData?.internal_summary;
  const customerSummary = aiUsageData?.customer_summary;

  const renderFeatureSection = (title, features) => {
    if (!features.length) return null;
    return (
      <>
        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>
          {title}
        </Text>
        {features.map((f) => renderFeatureDetailRow(f.feature, f))}
      </>
    );
  };

  const renderDetailRow = (label, value, extra = null) => (
    <div
      key={label}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}
    >
      <Text style={{ fontSize: '11px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
        {label}
      </Text>
      <Space size={4}>
        {extra}
        <Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>{value} reqs</Tag>
      </Space>
    </div>
  );

  const shareText = (share) =>
    typeof share === "number" ? `${(share * 100).toFixed(1)}%` : "—";

  const topProvider = aiUsageData?.by_provider?.[0];
  const topModel = aiUsageData?.by_model?.[0];
  const topFeature = aiUsageData?.by_feature?.[0];

  return (
    <div style={{ marginTop: 8 }}>
      {onAiUsageClassChange ? (
        <Space style={{ marginBottom: 12 }}>
          <Text type="secondary">{aiWindowLabel}</Text>
          <Radio.Group
            size="small"
            value={aiUsageClass}
            onChange={(event) => onAiUsageClassChange(event.target.value)}
            optionType="button"
            options={[
              { label: "Customer", value: "customer" },
              { label: "Internal", value: "internal" },
            ]}
          />
        </Space>
      ) : null}
      {aiLoading ? (
        <div style={{ padding: "24px 0", textAlign: "center" }}>
          <Spin tip="Loading AI usage..." />
        </div>
      ) : aiError ? (
        <Alert type="error" showIcon message={aiError} />
      ) : !aiUsageData?.overall ? (
        <Text type="secondary">No AI usage in this range.</Text>
      ) : (
    <Row gutter={[12, 12]}>
      {/* AI Usage Overview Card */}
      {aiUsageData?.overall && (
        <Col xs={24} sm={12} lg={8}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
              <Row align="middle" gutter={[16, 0]}>
                <Col span={24}>
                  <Text type="secondary" style={{ display: "block", fontSize: 12 }}>AI Usage Overview</Text>
                  <div style={metricValueStyle}>{formatNumber(aiUsageData.overall.total_requests)}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {aiUsageClass === "internal" ? "Internal" : "Customer"} requests, {aiWindowLabel}
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      <Col flex="none">
                        <Tooltip title="Total Cost"><Tag color="purple" icon={<DollarOutlined />} style={{ margin: 0 }}>{formatCost(aiUsageData.overall.total_cost)}</Tag></Tooltip>
                      </Col>
                      <Col flex="none">
                        <Tooltip title="Avg Tokens/Request"><Tag color="cyan" icon={<ThunderboltOutlined />} style={{ margin: 0 }}>{Math.round(aiUsageData.overall.avg_tokens_per_request)}</Tag></Tooltip>
                      </Col>
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowAiOverallDetails(!showAiOverallDetails)}
                          icon={showAiOverallDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto' }}>
                          {showAiOverallDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiOverallDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        {[
                          ['Total Cost', formatCost(aiUsageData.overall.total_cost)],
                          ['Total Tokens', formatNumber(aiUsageData.overall.total_tokens)],
                          ['Prompt Tokens', formatNumber(aiUsageData.overall.total_prompt_tokens)],
                          ['Completion Tokens', formatNumber(aiUsageData.overall.total_completion_tokens)],
                          ['Total Requests', formatNumber(aiUsageData.overall.total_requests)],
                          ['Avg Cost / Request', formatCost(aiUsageData.overall.avg_cost_per_request)],
                          ['Avg Tokens / Request', Math.round(aiUsageData.overall.avg_tokens_per_request).toLocaleString()],
                          ...(aiUsageData.by_usage_class || []).map((row) => [
                            `COGS — ${row.usage_class}`,
                            `${formatCost(row.total_cost)} (${formatNumber(row.total_requests)} req)`,
                          ]),
                        ].map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <Text style={{ fontSize: '11px' }}>{k}</Text>
                            <Text strong style={{ fontSize: '11px' }}>{v}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
              </Row>
          </Card>
        </Col>
      )}

      {/* AI by Provider Card */}
      {aiUsageData?.by_provider?.length > 0 && (
        <Col xs={24} sm={12} lg={8}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
              <Row align="middle" gutter={[16, 0]}>
                <Col span={24}>
                  <Text type="secondary" style={{ display: "block", fontSize: 12 }}>AI by Provider</Text>
                  <div style={metricValueStyle}>{topProvider.provider}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatNumber(topProvider.total_requests)} requests · {shareText(topProvider.request_share)} of requests · {formatCost(topProvider.total_cost)}
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      {aiUsageData.by_provider.slice(0, 2).map((p) => (
                        <Col flex="none" key={p.provider}>
                          <Tooltip title={`${formatNumber(p.total_requests)} reqs · ${formatCost(p.total_cost)}`}>
                            <Tag color="blue" style={{ margin: 0, textTransform: 'capitalize' }}>{p.provider}</Tag>
                          </Tooltip>
                        </Col>
                      ))}
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowAiProviderDetails(!showAiProviderDetails)}
                          icon={showAiProviderDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto' }}>
                          {showAiProviderDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiProviderDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        {aiUsageData.by_provider.map((p) => renderDetailRow(
                          <span style={{ textTransform: 'capitalize' }}>{p.provider}</span>,
                          formatNumber(p.total_requests),
                          <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>{formatCost(p.total_cost)}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
              </Row>
          </Card>
        </Col>
      )}

      {internalSummary && (
        <Col xs={24} sm={12} lg={8}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
              <Row align="middle" gutter={[16, 0]}>
                <Col span={24}>
                  <Text type="secondary" style={{ display: "block", fontSize: 12 }}>Internal AI Cost</Text>
                  <div style={metricValueStyle}>{formatCost(internalSummary.total_cost)}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatNumber(internalSummary.total_requests)} admin/staff requests, {aiWindowLabel}
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      <Col flex="none">
                        <Tooltip title="Internal token consumption">
                          <Tag color="purple" icon={<ThunderboltOutlined />} style={{ margin: 0 }}>
                            {formatNumber(internalSummary.total_tokens)}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="none">
                        <Tooltip title="Share of AI spend in this window">
                          <Tag color="magenta" icon={<DollarOutlined />} style={{ margin: 0 }}>
                            {shareText(internalSummary.cost_share)}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setShowAiInternalDetails(!showAiInternalDetails)}
                          icon={showAiInternalDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto' }}
                        >
                          {showAiInternalDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiInternalDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {renderSummaryRow('Internal cost', formatCost(internalSummary.total_cost))}
                        {renderSummaryRow('Internal requests', formatNumber(internalSummary.total_requests))}
                        {renderSummaryRow('Internal tokens', formatNumber(internalSummary.total_tokens))}
                        {renderSummaryRow('Top internal users', formatNumber(aiUsageData?.top_internal_users?.length || 0))}
                        {(aiUsageData?.top_internal_users || []).map((user) => renderSummaryRow(
                          user.user_email,
                          formatNumber(user.total_requests),
                          <Tag color="magenta" style={{ margin: 0, fontSize: '10px' }}>{formatCost(user.total_cost)}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
              </Row>
          </Card>
        </Col>
      )}

      {customerSummary && (
        <Col xs={24} sm={12} lg={8}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
              <Row align="middle" gutter={[16, 0]}>
                <Col span={24}>
                  <Text type="secondary" style={{ display: "block", fontSize: 12 }}>Customer AI Usage</Text>
                  <div style={metricValueStyle}>{formatNumber(customerSummary.total_requests)}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatCost(customerSummary.total_cost)} customer spend, {aiWindowLabel}
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      <Col flex="none">
                        <Tooltip title="Customer token consumption">
                          <Tag color="blue" icon={<ThunderboltOutlined />} style={{ margin: 0 }}>
                            {formatNumber(customerSummary.total_tokens)}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="none">
                        <Tooltip title="Share of AI spend in this window">
                          <Tag color="green" icon={<DollarOutlined />} style={{ margin: 0 }}>
                            {shareText(customerSummary.cost_share)}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setShowAiCustomerDetails(!showAiCustomerDetails)}
                          icon={showAiCustomerDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto' }}
                        >
                          {showAiCustomerDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiCustomerDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {renderSummaryRow('Customer cost', formatCost(customerSummary.total_cost))}
                        {renderSummaryRow('Customer requests', formatNumber(customerSummary.total_requests))}
                        {renderSummaryRow('Customer tokens', formatNumber(customerSummary.total_tokens))}
                        {renderSummaryRow('Top customer users', formatNumber(aiUsageData?.top_customer_users?.length || 0))}
                        {(aiUsageData?.top_customer_users || []).slice(0, 3).map((user) => renderSummaryRow(
                          user.user_email,
                          formatNumber(user.total_requests),
                          <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>{formatCost(user.total_cost)}</Tag>
                        ))}
                        {(aiUsageData?.customer_feature_highlights || []).slice(0, 3).map((feature) => renderSummaryRow(
                          getFeatureDisplayName(feature.feature_name),
                          formatNumber(feature.total_requests),
                          <Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>{formatCost(feature.total_cost)}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
              </Row>
          </Card>
        </Col>
      )}

      {/* AI by Model Card */}
      {aiUsageData?.by_model?.length > 0 && (
        <Col xs={24} sm={12} lg={8}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
              <Row align="middle" gutter={[16, 0]}>
                <Col span={24}>
                  <Text type="secondary" style={{ display: "block", fontSize: 12 }}>AI by Model</Text>
                  <div style={metricValueStyle}>{topModel.model}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatNumber(topModel.total_requests)} requests · {shareText(topModel.request_share)} of requests · {formatCost(topModel.total_cost)}
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      {aiUsageData.by_model.slice(0, 2).map((m) => (
                        <Col flex="none" key={`${m.provider}-${m.model}`}>
                          <Tooltip title={`${m.provider} · ${formatNumber(m.total_requests)} reqs · ${formatCost(m.total_cost)}`}>
                            <Tag color="cyan" style={{ margin: 0, maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.model}</Tag>
                          </Tooltip>
                        </Col>
                      ))}
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowAiModelDetails(!showAiModelDetails)}
                          icon={showAiModelDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto' }}>
                          {showAiModelDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiModelDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        {aiUsageData.by_model.map((m) => renderDetailRow(
                          `${m.model}`,
                          formatNumber(m.total_requests),
                          <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>{formatCost(m.total_cost)}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
              </Row>
          </Card>
        </Col>
      )}

      {/* AI by Feature Card */}
      {aiUsageData?.by_feature?.length > 0 && (() => {
        const { polars: polarsFeatures, paid: paidFeatures } = partitionFeaturesByEngine(aiUsageData.by_feature);
        return (
        <Col xs={24} sm={12} lg={8}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
              <Row align="middle" gutter={[16, 0]}>
                <Col span={24}>
                  <Text type="secondary" style={{ display: "block", fontSize: 12 }}>AI by Feature</Text>
                  <div style={metricValueStyle}>{getFeatureShortLabel(topFeature.feature)}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatNumber(topFeature.total_requests)} requests · {shareText(topFeature.request_share)} of requests · {formatCost(topFeature.total_cost)}
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Space size={4} wrap>
                      <Tooltip title="Smart AI Summary: statistical summaries, no LLM cost">
                        <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>Smart AI Summary ({polarsFeatures.length})</Tag>
                      </Tooltip>
                      <Tooltip title="OpenAI, Gemini, OpenRouter, and other paid providers">
                        <Tag color="purple" style={{ margin: 0, fontSize: '10px' }}>Paid LLM ({paidFeatures.length})</Tag>
                      </Tooltip>
                    </Space>
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      {aiUsageData.by_feature.slice(0, 2).map((f) => (
                        <Col flex="none" key={f.feature}>
                          {renderFeatureTag(f.feature, f)}
                        </Col>
                      ))}
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowAiFeatureDetails(!showAiFeatureDetails)}
                          icon={showAiFeatureDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto' }}>
                          {showAiFeatureDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiFeatureDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        {renderFeatureSection('Smart AI Summary (free)', polarsFeatures)}
                        {renderFeatureSection('Paid LLM providers', paidFeatures)}
                      </Space>
                    </div>
                  )}
                </Col>
              </Row>
          </Card>
        </Col>
        );
      })()}
    </Row>
      )}
    </div>
  );
};